import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { Sparkles, Copy, Download, Check, FileText } from 'lucide-react'
import { generatePressPack } from '@/lib/claude'
import { Button, Input, Card, Toggle } from '@/components/ui'
import { captureError } from '@/lib/sentry'
import type { PressTone } from '@/types'

const schema = z.object({
  event_name:     z.string().min(1, 'Event name required'),
  event_date:     z.string().optional(),
  event_venue:    z.string().optional(),
  organiser_name: z.string().optional(),
})
type FormData = z.infer<typeof schema>

const TONES: { value: PressTone; label: string; desc: string }[] = [
  { value: 'editorial', label: 'Editorial', desc: 'Magazine feature tone' },
  { value: 'formal',    label: 'Formal',    desc: 'Official press release' },
  { value: 'hype',      label: 'Hype',      desc: 'High-energy promo copy' },
]

export default function PressPage() {
  const [tone,       setTone]       = useState<PressTone>('editorial')
  const [highlights, setHighlights] = useState(true)
  const [loading,    setLoading]    = useState(false)
  const [output,     setOutput]     = useState('')
  const [copied,     setCopied]     = useState(false)
  const [error,      setError]      = useState<string | null>(null)

  const { register, handleSubmit, getValues, formState: { errors } } = useForm<FormData>({
    resolver: zodResolver(schema),
  })

  const onSubmit = async (data: FormData) => {
    setLoading(true)
    setError(null)
    try {
      const pack = await generatePressPack({
        event_name:         data.event_name,
        event_date:         data.event_date ?? '',
        event_venue:        data.event_venue ?? '',
        organiser_name:     data.organiser_name ?? '',
        tone,
        include_highlights: highlights,
      })
      setOutput(pack)
    } catch (err) {
      captureError(err)
      setError('Could not generate press pack. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const copy = async () => {
    await navigator.clipboard.writeText(output)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const download = () => {
    const name = getValues('event_name').replace(/\s+/g, '-').toLowerCase()
    const blob = new Blob([output], { type: 'text/plain' })
    const url  = URL.createObjectURL(blob)
    const a    = document.createElement('a')
    a.href = url
    a.download = `${name}-press-pack.txt`
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <Input
          label="Event name *"
          placeholder="Ibiza Opening Weekend 2026"
          error={errors.event_name?.message}
          {...register('event_name')}
        />

        <div className="grid grid-cols-2 gap-3">
          <Input label="Date" placeholder="29 May 2026" {...register('event_date')} />
          <Input label="Venue" placeholder="Pacha, Ibiza"  {...register('event_venue')} />
        </div>

        <Input label="Organiser / Brand" placeholder="The Room Network" {...register('organiser_name')} />

        {/* Tone selector */}
        <div className="space-y-2">
          <label className="text-xs text-room-text font-medium">Tone</label>
          <div className="grid grid-cols-3 gap-2">
            {TONES.map(t => (
              <button
                key={t.value}
                type="button"
                onClick={() => setTone(t.value)}
                className={`rounded-xl border p-3 text-left transition-all ${
                  tone === t.value
                    ? 'border-room-accent bg-room-accent/10 text-white'
                    : 'border-room-border bg-room-surface text-room-muted hover:border-room-muted'
                }`}
              >
                <div className="text-sm font-semibold">{t.label}</div>
                <div className="text-xs opacity-70 mt-0.5">{t.desc}</div>
              </button>
            ))}
          </div>
        </div>

        <Toggle
          checked={highlights}
          onChange={() => setHighlights(h => !h)}
          label="Include media highlights section"
        />

        {error && <p className="text-room-error text-sm">{error}</p>}

        <Button
          type="submit"
          size="lg"
          loading={loading}
          icon={<Sparkles size={16} />}
          className="w-full"
        >
          Generate Press Pack
        </Button>
      </form>

      {/* Output */}
      {output && (
        <Card className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm font-semibold text-room-accent flex items-center gap-1.5">
              <FileText size={14} /> Press Pack Ready
            </span>
            <div className="flex gap-2">
              <Button variant="secondary" size="sm" icon={copied ? <Check size={12} /> : <Copy size={12} />} onClick={copy}>
                {copied ? 'Copied!' : 'Copy'}
              </Button>
              <Button variant="secondary" size="sm" icon={<Download size={12} />} onClick={download}>
                .txt
              </Button>
            </div>
          </div>
          <pre className="text-sm text-room-text whitespace-pre-wrap leading-relaxed max-h-72 overflow-y-auto font-sans">
            {output}
          </pre>
        </Card>
      )}
    </div>
  )
}
